import { useEffect, useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
// Hooks
import { useAuth } from '@/hooks/useAuth';
import { useAppInfo } from '@/providers/AppInfoProvider';
import { useWallet } from '@/hooks/useWallet';
// Components
import { Page } from '@/components/common';
import { OfferCard, ReferralCard, ShareModal, NoWalletCard } from './components';
// Hooks
import { useWebiny } from '@/providers/WebinyProvider';

// Utils
import { getMemberReferrals } from '@/resources/membership-service.resource';
import { formatAmount, formatDate } from '@/utils/index';
// Types
import { TableRow, ReferralData } from './types';

export default function Home(): JSX.Element {
  const { fetcher, memberData } = useAuth();
  const { walletCoinState, coreWalletCoinState, hasError, coreHasError } = useWallet();
  const { currentOffers, gtagReferralShareEvent }: any = useAppInfo();
  const { pageHeader, shareOfferPopup } = useWebiny();
  const [currentLink, setCurrentLink] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tableData, setTableData] = useState<Array<TableRow>>([]);

  const serverError = hasError.server || coreHasError.server;
  const isWalletLoading = walletCoinState?.loading || coreWalletCoinState?.loading;
  const showWalletSetup =
    !isWalletLoading &&
    !walletCoinState?.hasWallet &&
    !coreWalletCoinState?.hasWallet &&
    !serverError;

  const shareName: boolean = memberData?.optInShareName;

  const hasTableData: boolean = !isLoading && tableData.length > 0;

  useEffect(() => {
    if (!memberData?.userId) return;
    fetcher(getMemberReferrals())
      .then((data: ReferralData[]) => {
        const formatted = data.map((datum) => ({
          date: formatDate(new Date(datum.dateCreated), 'short', false),
          name: datum.memberName,
          smartNodesPurchased: formatAmount(datum.smartNodePurchased),
          liteNodesPurchased: formatAmount(datum.liteNodePurch),
          // Brand to work with Chain team to get points awarded
          // pointsAwarded: formatAmount(
          //   datum.smartNodePurchased * 100 + datum.liteNodePurch * 10
          // ),
        }));
        setTableData(formatted);
        setIsLoading(false);
      })
      .catch(() => setShowError(true));
  }, [memberData?.userId]);

  const handleShareClick = (offer): void => {
    setCurrentLink(offer.url);
    gtagReferralShareEvent({ offer, sponsor: memberData?.memberNumber });
    setShowModal(true);
  };

  const closeModal = (): void => {
    setCurrentLink('');
    setShowModal(false);
  };

  return (
    <>
      <Page>
        <Typography variant="h2">
          {`${pageHeader.title} ${memberData?.firstName} ${memberData?.lastName}`}
        </Typography>
        {showWalletSetup ? <NoWalletCard /> : null}
        <OfferCard handleShareClick={handleShareClick} />
        <ReferralCard
          hasTableData={hasTableData}
          shareName={shareName}
          showError={showError}
          tableData={tableData}
        />
        <Dialog open={showModal} onClose={closeModal}>
          <Box sx={{ position: 'relative' }}>
            <ShareModal
              currentLink={currentLink}
              closeModal={closeModal}
              offer={currentOffers.length > 0}
              cmsData={shareOfferPopup}
            />
          </Box>
        </Dialog>
      </Page>
    </>
  );
}

Home.protectedRoute = true;
